import { areFieldsChanged } from './areFieldsChanged';
import { isExecPolicyChanged } from './isExecPolicyChanged';
import { createBlockChecker } from '../createBlockChecker';
import { isDelayChanged } from '../isDelayChanged';

/**
 * Determines if a forward notifications block has been edited by comparing current and initial states.
 *
 * Checks for changes in three key areas:
 * 1. Fields - any modifications to field values, additions, or removals
 * 2. Delay configuration - changes to delay timing or structure
 * 3. Execution policy - changes to how the block executes (wait_result, ignore_result, check_result, or inherit)
 *
 * Returns true if ANY of these areas have changed, false if the blocks are identical or if either
 * block lacks the required fields array.
 *
 * @param {Object} currentBlock - The current state of the forward notifications block
 * @param {string} currentBlock._id - Block identifier
 * @param {Array} currentBlock.fields - Array of field objects with name, type, and value
 * @param {Object} [currentBlock.delay] - Optional delay configuration
 * @param {string} [currentBlock.exec_policy] - Optional execution policy ('wait_result', 'ignore_result', 'check_result')
 * @param {Object} currentBlock.blockOptions - Block configuration with method details
 *
 * @param {Object} initialBlock - The initial/original state of the block to compare against
 * @param {string} initialBlock._id - Block identifier (should match currentBlock._id)
 * @param {Array} initialBlock.fields - Array of field objects from initial state
 * @param {Object} [initialBlock.delay] - Optional delay configuration from initial state
 * @param {string} [initialBlock.exec_policy] - Optional execution policy from initial state
 * @param {Object} initialBlock.blockOptions - Block configuration from initial state
 *
 * @returns {boolean} Returns true if any changes detected, false if identical or invalid input
 *
 * @example
 * // Detect field value changes
 * const initial = {
 *   _id: '123',
 *   fields: [
 *     { name: 'device', type: 'device', value: 'device-001' },
 *     { name: 'template', type: 'templateId', value: 'template-123' }
 *   ]
 * };
 * const current = {
 *   _id: '123',
 *   fields: [
 *     { name: 'device', type: 'device', value: 'device-002' }, // changed
 *     { name: 'template', type: 'templateId', value: 'template-123' }
 *   ]
 * };
 * validationStateForwardNotificationsIsEdited(current, initial); // true
 *
 * @example
 * // Detect delay changes
 * const initial = {
 *   fields: [{ name: 'device', value: 'device-001' }],
 *   delay: { minutes: 5 }
 * };
 * const current = {
 *   fields: [{ name: 'device', value: 'device-001' }],
 *   delay: { minutes: 10 } // delay changed
 * };
 * validationStateForwardNotificationsIsEdited(current, initial); // true
 *
 * @example
 * // Detect execution policy changes
 * const initial = {
 *   fields: [{ name: 'device', value: 'device-001' }],
 *   exec_policy: 'wait_result'
 * };
 * const current = {
 *   fields: [{ name: 'device', value: 'device-001' }],
 *   exec_policy: 'ignore_result' // policy changed
 * };
 * validationStateForwardNotificationsIsEdited(current, initial); // true
 *
 * @example
 * // No changes detected
 * const block = {
 *   fields: [{ name: 'device', value: 'device-001' }],
 *   delay: { minutes: 5 },
 *   exec_policy: 'wait_result'
 * };
 * validationStateForwardNotificationsIsEdited(block, block); // false
 *
 * @example
 * // Invalid input - missing fields
 * validationStateForwardNotificationsIsEdited({}, { fields: [] }); // false
 * validationStateForwardNotificationsIsEdited({ fields: [] }, {}); // false
 * validationStateForwardNotificationsIsEdited(null, null); // false
 *
 * @example
 * // Real forward notifications block comparison
 * const initialBlock = {
 *   _id: '68ca8078e88abe1f815d9ca9',
 *   fields: [
 *     { name: 'device', type: 'device', value: '68ca8077e88abe1db3f6242e' },
 *     { name: 'notificationTemplate', type: 'templateId', value: 'template-123' },
 *     { name: 'targets', type: 'array', value: [{ type: 'account', channels: ['email'] }] }
 *   ],
 *   exec_policy: 'wait_result'
 * };
 * const currentBlock = {
 *   _id: '68ca8078e88abe1f815d9ca9',
 *   fields: [
 *     { name: 'device', type: 'device', value: '68ca8077e88abe1db3f6242e' },
 *     { name: 'notificationTemplate', type: 'templateId', value: 'template-456' }, // changed
 *     { name: 'targets', type: 'array', value: [{ type: 'account', channels: ['email', 'push'] }] } // changed
 *   ],
 *   exec_policy: 'wait_result'
 * };
 * validationStateForwardNotificationsIsEdited(currentBlock, initialBlock); // true
 */
export const validationStateForwardNotificationsIsEdited = (currentBlock, initialBlock) => {
    if (currentBlock?.fields && initialBlock?.fields) {
        return createBlockChecker(areFieldsChanged, isDelayChanged, isExecPolicyChanged)(currentBlock, initialBlock);
    }

    return false;
};
